import React, { useEffect, useState } from 'react';
import Overlay from './Overlay.jsx';
import { ColumnsView, SideView, containerContents } from './PackingPicture.jsx';
import { columnsPhrase, fullLayersCaption, fullLayersTab, inchesText, nounOf, num, stackLayers, topLayerCaption, topLayerTab } from './palletCheckData';
import { C, tab } from './styles';

// The enlarged pictures: a stack's top view with numbered spots (tabs: the full layers / the top layer /
// the side), and a carton's inside drawn to scale with each zone labelled.

function StackGrid({ layers, view, width }) {
  const cellW = width / layers.cols;
  const height = Math.round(cellW * layers.rows);
  return (
    <div data-testid="stack-grid" data-view={view}
      style={{ width, height, display: 'grid', gap: 2, background: C.night,
        gridTemplateColumns: `repeat(${layers.cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${layers.rows}, minmax(0, 1fr))` }}>
      {layers.cells.map((c) => {
        const filled = view === 'top' ? c.top : c.height > 0;
        const style = filled
          ? { background: view === 'top' ? C.amber : C.steel, color: C.ink, fontSize: cellW < 22 ? 9 : 12, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center' }
          : { background: 'transparent', border: '2px dashed #6B7280', boxSizing: 'border-box' };
        return (
          <div key={c.n} data-testid="spot" data-filled={filled ? 'true' : 'false'} style={style}>
            {filled ? c.n : ''}
          </div>
        );
      })}
    </div>
  );
}

export function StackLightbox({ open, onClose, data, container, zone, initialView = null }) {
  const layers = zone ? stackLayers(zone) : null;
  const firstTab = layers ? fullLayersTab(layers) : null;
  const startView = initialView || (firstTab ? 'full' : 'top');
  const [view, setView] = useState(startView);
  useEffect(() => { if (open) setView(startView); }, [open, startView]);
  if (!open || !layers) return null;

  const { noun } = nounOf(data);
  const pallet = data.pallet || {};
  const p = container.on_pallet || {};
  const unitWord = zone.unit_word || `${zone.units_planned} ${(data.counts && data.counts.unit) || 'pcs'}`;
  const what = zone.assembly_name || zone.product_id;
  const height = inchesText(zone.height_in);
  const plate = zone.product_id || 'pieces';
  const gridWidth = Math.min(320, typeof window !== 'undefined' ? window.innerWidth - 72 : 320);
  const sideText = `${layers.maxHigh} layer${layers.maxHigh === 1 ? '' : 's'} of ${plate}${height ? `, about ${height} in of steel` : ''}. `
    + 'Not to scale: layers drawn thick so they can be counted.';

  const back = (
    <div style={{ padding: '10px 16px 18px', display: 'flex', justifyContent: 'flex-end' }}>
      <button type="button" onClick={onClose} style={{ background: 'none', border: 0, color: '#93C5FD', fontFamily: 'inherit', fontSize: 15, minHeight: 44, cursor: 'pointer' }}>Back to the {noun}</button>
    </div>
  );
  const subtitle = `${what} · ${unitWord} · ${(container.label || 'stack').toLowerCase()}`;

  if (!layers.grid) {
    // No tile grid on file: its loose columns and the API's words, no made-up grid and no layer tabs.
    const phrase = columnsPhrase(layers);
    return (
      <Overlay open={open} onClose={onClose} title={pallet.short_id || 'Pallet'} subtitle={subtitle} testId="stack-lightbox">
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 16 }}>
          <ColumnsView zone={zone} width={Math.min(320, gridWidth)} height={220} labels />
          <div data-testid="layer-caption" style={{ background: C.panel, borderRadius: 12, padding: '12px 14px', width: '100%', boxSizing: 'border-box', fontSize: 15, lineHeight: 1.4 }}>
            {`${phrase}. ${zone.stack_phrase ? `(${zone.stack_phrase}.) ` : ''}JobQ has no layout on file for this stack: stand them as loose stacks, each as high as shown.`}
          </div>
          {zone.hand_placed && <div style={{ fontSize: 14, color: '#FCD34D' }}>Put here by hand: the picture shows the plan, not how they were stacked.</div>}
        </div>
        {back}
      </Overlay>
    );
  }

  return (
    <Overlay open={open} onClose={onClose} title={pallet.short_id || 'Pallet'} subtitle={subtitle}
      testId="stack-lightbox">
      <div style={{ padding: '0 16px', display: 'grid', gridTemplateColumns: `repeat(${firstTab ? 3 : 2}, minmax(0, 1fr))`, gap: 6 }} role="tablist">
        {firstTab && <button type="button" role="tab" aria-selected={view === 'full'} style={tab(view === 'full')} onClick={() => setView('full')}>{firstTab}</button>}
        <button type="button" role="tab" aria-selected={view === 'top'} style={tab(view === 'top')} onClick={() => setView('top')}>{topLayerTab(layers)}</button>
        <button type="button" role="tab" aria-selected={view === 'side'} style={tab(view === 'side')} onClick={() => setView('side')}>Side</button>
      </div>

      {view === 'side' ? (
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '16px' }}>
          <SideView zone={zone} width={Math.min(330, gridWidth + 10)} barHeight={22} labels />
          <div style={{ fontSize: 15, textAlign: 'center', color: C.track }} data-testid="side-caption">{sideText}</div>
        </div>
      ) : (
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 13, color: C.dim }}>
              {p.h ? `${num(p.h)} in deep` : ''} ({layers.rows} deep)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
              <StackGrid layers={layers} view={view} width={gridWidth} />
              <div style={{ fontSize: 13, color: C.dim, textAlign: 'center' }}>
                {p.w ? `${num(p.w)} in across` : ''} ({layers.cols} across){pallet.length_in && pallet.width_in ? ` on a ${Math.round(pallet.length_in)} × ${Math.round(pallet.width_in)} pallet` : ''}
              </div>
            </div>
          </div>
          <div data-testid="layer-caption" style={{ background: C.panel, borderRadius: 12, padding: '12px 14px', width: '100%', boxSizing: 'border-box', fontSize: 15, lineHeight: 1.4 }}>
            {view === 'top' ? topLayerCaption(layers) : fullLayersCaption(layers)}
          </div>
          {zone.stack_phrase && <div style={{ fontSize: 14, color: C.dim }}>{zone.stack_phrase}</div>}
          {zone.hand_placed && <div style={{ fontSize: 14, color: '#FCD34D' }}>Put here by hand: the picture shows the plan, not how they were stacked.</div>}
        </div>
      )}
      {back}
    </Overlay>
  );
}

const ZONE_COLORS = ['#9BA3AE', '#93C5FD', '#FCD34D', '#86EFAC', '#F9A8D4', '#C4B5FD'];

/** The one-line sentence for a carton. */
export function boxSentence(container) {
  const zones = container.zones || [];
  const items = (container.purchased_items || []).length;
  if (!zones.length) return items ? 'Only bought items go in this box (listed below).' : 'Nothing is planned in this box.';
  return zones.map((z) => {
    const what = z.assembly_name || z.product_id;
    const how = z.stack_phrase ? `: ${z.stack_phrase}` : '';
    const short = z.short_accepted ? ' The order accepts it short.' : '';
    return `${z.unit_word || z.units_planned} of ${what}${how} (${z.units_packed ?? 0} packed so far).${short}`;
  }).join(' ') + (items ? ' Plus the bought items listed below.' : '');
}

export function BoxLightbox({ open, onClose, data, container }) {
  if (!open || !container) return null;
  const zones = container.zones || [];
  let L = Number(container.interior && container.interior.l) || 0;
  let W = Number(container.interior && container.interior.w) || 0;
  if (!(L > 0 && W > 0)) {
    for (const z of zones) { L = Math.max(L, (Number(z.x) || 0) + (Number(z.w) || 0)); W = Math.max(W, (Number(z.y) || 0) + (Number(z.h) || 0)); }
  }
  L = L || 1; W = W || 1;
  const drawW = Math.min(300, typeof window !== 'undefined' ? window.innerWidth - 48 : 300);
  const drawH = Math.round((drawW * W) / L);
  const unit = (data.counts && data.counts.unit) || 'pcs';
  const { noun } = nounOf(data);
  const bought = container.purchased_items || [];
  const chips = [container.status, [container.sku, container.box_name].filter(Boolean).join(' · '), container.weight_lbs != null ? `${num(container.weight_lbs, 0)} lb` : null].filter(Boolean);

  return (
    <Overlay open={open} onClose={onClose} title={`${container.label || 'Box'}${container.short_id ? ` · ${container.short_id}` : ''}`}
      subtitle={`${containerContents(container, unit)} on ${(data.pallet && data.pallet.short_id) || `this ${noun}`}`} testId="box-lightbox">
      <div style={{ padding: '0 16px 18px', display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignSelf: 'stretch' }}>
          {chips.map((c) => <span key={c} style={{ fontSize: 13, fontWeight: 600, background: C.panel, borderRadius: 6, padding: '4px 8px' }}>{c}</span>)}
        </div>
        {container.riding_from && container.riding_from.order_number && (
          <div style={{ alignSelf: 'stretch', fontSize: 15, color: '#FCD34D' }}>
            This box belongs to {container.riding_from.order_number}{container.riding_from.customer_name ? ` for ${container.riding_from.customer_name}` : ''} and rides along. Check its label says so.
          </div>
        )}
        <div data-testid="box-interior" style={{ width: drawW, height: drawH, background: '#EADBC3', border: '2px solid #8A6A3F', borderRadius: 3, position: 'relative', boxSizing: 'content-box' }}>
          {zones.map((z, i) => (
            <div key={z.zone_id} data-testid={`box-zone-${z.zone_id}`}
              style={{ position: 'absolute', left: `${((Number(z.x) || 0) / L) * 100}%`, top: `${((Number(z.y) || 0) / W) * 100}%`,
                width: `${((Number(z.w) || 0) / L) * 100}%`, height: `${((Number(z.h) || 0) / W) * 100}%`,
                background: ZONE_COLORS[i % ZONE_COLORS.length], border: `1px solid ${C.ink}`, boxSizing: 'border-box', borderRadius: 2, color: C.ink,
                fontSize: 12, fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', overflow: 'hidden', padding: 2 }}>
              <span>{z.product_id}</span>
              <span>{z.stack_phrase || z.unit_word}</span>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 13, color: C.dim }}>
          Inside, from above{container.interior && container.interior.l ? ` · ${num(container.interior.l)} in wide × ${num(container.interior.w)} in deep` : ''}
        </div>
        {bought.length > 0 && (
          <div data-testid="box-purchased" style={{ alignSelf: 'stretch', background: C.panel, borderRadius: 12, padding: '12px 14px' }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Bought items in this box ({bought.reduce((n, i) => n + (Number(i.quantity) || 0), 0)})</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 15 }}>
              {bought.map((i, k) => (
                <li key={`${i.product_id}-${k}`}><b>{i.quantity} ×</b> {i.product_id}{i.description ? ` · ${i.description}` : ''}</li>
              ))}
            </ul>
          </div>
        )}
        <div data-testid="box-sentence" style={{ background: C.panel, borderRadius: 12, padding: '12px 14px', alignSelf: 'stretch', fontSize: 16, lineHeight: 1.4 }}>{boxSentence(container)}</div>
        <button type="button" onClick={onClose} style={{ alignSelf: 'flex-end', background: 'none', border: 0, color: '#93C5FD', fontFamily: 'inherit', fontSize: 15, minHeight: 44, cursor: 'pointer' }}>Back to the {noun}</button>
      </div>
    </Overlay>
  );
}
